/**
 * Simple event emitter
 * @link https://github.com/DjonnyX/rcx-virtual-list/tree/main/src/lib/src/utils/eventEmitter.ts
 * @author Evgenii Grebennikov
 * @email djonnyx@gmail.com
 */
export class EventEmitter {
    constructor() {
        this._listeners = {};
        this._disposed = false;
    }
    /**
     * Emits the event
     */
    dispatch(event, ...args) {
        const ctx = this;
        const listeners = this._listeners[event];
        if (Array.isArray(listeners)) {
            for (let i = 0, l = listeners.length; i < l; i++) {
                const listener = listeners[i];
                if (listener) {
                    listener.apply(ctx, args);
                }
            }
        }
    }
    /**
     * Emits the event async
     */
    dispatchAsync(event, ...args) {
        queueMicrotask(() => {
            if (this._disposed) {
                return;
            }
            this.dispatch(event, ...args);
        });
    }
    /**
     * Returns true if the event listener is already subscribed.
     */
    hasEventListener(eventName, handler) {
        const event = eventName;
        if (this._listeners.hasOwnProperty(event)) {
            const listeners = this._listeners[event];
            const index = listeners.findIndex(v => v === handler);
            if (index > -1) {
                return true;
            }
        }
        return false;
    }
    /**
     * Add event listener
     */
    addEventListener(eventName, handler) {
        const event = eventName;
        if (!this._listeners.hasOwnProperty(event)) {
            this._listeners[event] = [];
        }
        this._listeners[event].push(handler);
    }
    /**
     * Remove event listener
     */
    removeEventListener(eventName, handler) {
        const event = eventName;
        if (!this._listeners.hasOwnProperty(event)) {
            return;
        }
        const listeners = this._listeners[event], index = listeners.findIndex(v => v === handler);
        if (index > -1) {
            listeners.splice(index, 1);
            if (listeners.length === 0) {
                delete this._listeners[event];
            }
        }
    }
    /**
     * Remove all listeners
     */
    removeAllListeners() {
        const events = Object.keys(this._listeners);
        while (events.length > 0) {
            const event = events.pop();
            if (event) {
                const listeners = this._listeners[event];
                if (Array.isArray(listeners)) {
                    while (listeners.length > 0) {
                        const listener = listeners.pop();
                        if (listener) {
                            this.removeEventListener(event, listener);
                        }
                    }
                }
            }
        }
    }
    /**
     * Method of destroying handlers
     */
    dispose() {
        this._disposed = true;
        this.removeAllListeners();
    }
}
